// Library imports
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { FC, useEffect, useState } from 'react';
import store, { RootState } from '../../../store';
// Local imports
import useQuery from 'hooks/useQuery';
import SearchField from '../Shared/SearchField';
import ContactsTabOption from './ContactsTabOption';
import BouncedContactsList from './BouncedContactsList';
import { ANALYTICS_DEFAULT_DATES_FILTER } from './utils';
import { moveSelectedFiltersToApplied } from '../actions';
import DeliveredContactsList from './DeliveredContactsList';
import UnsubscribedContactsList from './UnsubscribedContactsList';
import ExportAnalyticsPopup from '../Shared/ExportAnalyticsPopup';
import ContactsByFilterComponent from './ContactsByFilterComponent';
import BounceTypeFilterComponent from './BounceTypeFilterComponent';
import { ANALYTICS_FILTERS, CONTACT_ANALYTICS } from 'constants/types';
import ContactsDashboard from './ContactsInner/Dashboard/ContactsDashboard';
import { addCommaToNumber, getAllTimeDates, getQuarterDates, getQueryAccountId } from 'utils';
import EmailEngagement from './ContactsInner/EmailEngagement/EmailEngagement';
import { analyticsDatesFilterChange, selectAsset } from '../Dashboard/actions';
import ContactsContent from './ContactsInner/ContentEngagement/ContactsContent';
import { resetAllContactProps, resetContactSearch, resetAllTabsFilter, getConstantCampaignEmailId } from '../utils';
import { PATH_ANALYTICS, TAB_ANALYTICS, TAB_CONTACTS_ANALYTICS, TAB_ANALYTICS_INNER } from '../../../constants/paths';
import { getBouncedContactsList, getDeliveredContactsList, getUnsubscribedContactsList, setBounceFilter, setBounceFilterFirstTime } from './action';
import {
  getAnalyticsFilterId,
  getQueryCampaignId,
  getQueryEmailId,
  getQueryEmailIds,
  getQueryIsAIEmail,
  getQueryWorkflowId,
} from 'modules/EmailBrowser/utils';
import { resetContactsEmailEngagement } from './ContactsInner/EmailEngagement/actions';
import { useHistory } from 'react-router-dom';

interface IContactsWrapper {
  page?: string;
  tab?: string;
  setSearchedText: (value: string) => void;
  searchedText: string;
}
const ContactsWrapper: FC<IContactsWrapper> = (props: IContactsWrapper) => {
  // Props
  const { tab, page, searchedText, setSearchedText } = props;

  // Local States
  const [accountId, setAccountId] = useState(-1);
  const [firstRender, setFirstRender] = useState(true);

  // Redux States
  const filterReset = useSelector((state: RootState) => state.contactAnalytics.filterReset);
  const searchReset = useSelector((state: RootState) => state.contactAnalytics.searchReset);
  const bouncedPage = useSelector((state: RootState) => state.contactAnalytics.bouncedPage);
  const deliveredPage = useSelector((state: RootState) => state.contactAnalytics.deliveredPage);
  const total_bounced = useSelector((state: RootState) => state.contactAnalytics.total_bounced);
  const bouncedSorting = useSelector((state: RootState) => state.contactAnalytics.bouncedSorting);
  const isFilterClicked = useSelector((state: RootState) => state.analyticsFilters.filterApplied);
  const total_delivered = useSelector((state: RootState) => state.contactAnalytics.total_delivered);
  const deliveredSorting = useSelector((state: RootState) => state.contactAnalytics.deliveredSorting);
  const contactsByFilter = useSelector((state: RootState) => state.contactAnalytics.contactsByFilter);
  const unsubscribedPage = useSelector((state: RootState) => state.contactAnalytics.unsubscribedPage);
  const total_unsubscribed = useSelector((state: RootState) => state.contactAnalytics.total_unsubscribed);
  const unsubscribedSorting = useSelector((state: RootState) => state.contactAnalytics.unsubscribedSorting);
  const selectedCampaignIds = useSelector((state: RootState) => state.analyticsDashboard.selectedCampaignsIds);
  const AllCampaignsFilterCheck = useSelector((state: RootState) => state.analyticsDashboard.appliedCampaignsFilters.isAllCampaigns);
  const engagementEmails = useSelector((state: RootState) => state.emailsEngagement.emails);
  const { isShareProfile, isDailyIQ, isWeeklyAccountInsights } = useSelector((state: RootState) => state.analyticsDashboard.appliedCampaignsFilters);
  const isContactDashboard =
    page === TAB_ANALYTICS.CONTACTS &&
    (tab === TAB_ANALYTICS_INNER.DASHBOARD || tab === TAB_ANALYTICS_INNER.CONTENT || tab === TAB_ANALYTICS.EMAIL_ENGAGEMENT);

  const isContactListing =
    page === TAB_ANALYTICS.CONTACTS &&
    (tab === TAB_CONTACTS_ANALYTICS.DELIVERED || tab === TAB_CONTACTS_ANALYTICS.BOUNCED || tab === TAB_CONTACTS_ANALYTICS.UNSUBSCRIBED);

  // Local varables
  let query = useQuery();
  const history = useHistory();
  const tabArray = [
    {
      pillName: `Delivered ${deliveredPage !== 0 ? '(' + addCommaToNumber((total_delivered ?? 0).toString() ?? '') + ')' : ''}`,
      to:
        window.location.search.length > 0
          ? `/${PATH_ANALYTICS}/${TAB_ANALYTICS.CONTACTS}/${TAB_CONTACTS_ANALYTICS.DELIVERED}/${window.location.search}`
          : `/${PATH_ANALYTICS}/${TAB_ANALYTICS.CONTACTS}/${TAB_CONTACTS_ANALYTICS.DELIVERED}`,
      isActive: tab === undefined || tab === TAB_CONTACTS_ANALYTICS.DELIVERED,
      onClick: () => {},
      idBtn: 'delivered-tab',
    },
    {
      pillName: `Bounced ${bouncedPage !== 0 ? '(' + addCommaToNumber((total_bounced ?? 0).toString() ?? '') + ')' : ''}`,
      to:
        window.location.search.length > 0
          ? `/${PATH_ANALYTICS}/${TAB_ANALYTICS.CONTACTS}/${TAB_CONTACTS_ANALYTICS.BOUNCED}/${window.location.search}`
          : `/${PATH_ANALYTICS}/${TAB_ANALYTICS.CONTACTS}/${TAB_CONTACTS_ANALYTICS.BOUNCED}`,
      isActive: tab === TAB_CONTACTS_ANALYTICS.BOUNCED,
      onClick: () => {},
      idBtn: 'bounced-tab',
    },
    {
      pillName: `Unsubscribed ${unsubscribedPage !== 0 ? '(' + addCommaToNumber((total_unsubscribed ?? 0).toString() ?? '') + ')' : ''}`,
      to:
        window.location.search.length > 0
          ? `/${PATH_ANALYTICS}/${TAB_ANALYTICS.CONTACTS}/${TAB_CONTACTS_ANALYTICS.UNSUBSCRIBED}/${window.location.search}`
          : `/${PATH_ANALYTICS}/${TAB_ANALYTICS.CONTACTS}/${TAB_CONTACTS_ANALYTICS.UNSUBSCRIBED}`,
      isActive: tab === TAB_CONTACTS_ANALYTICS.UNSUBSCRIBED,
      onClick: () => {},
      idBtn: 'unsubscribed-tab',
    },
  ];

  // Component Logic
  const getParams = (tabValue?: string, resetsearch?: boolean, isAccount?: boolean) => {
    let analyticsDashboard = store.getState().analyticsDashboard;
    let analyticsGraph = store.getState().analyticsGraph;
    let bounceFilter = store.getState().contactAnalytics.bounceFilter;
    let { appliedCampaignIds, appliedAssetsIds, appliedWorkflowsIds, excludeCampaignsIds, excludeAssetsIds } = analyticsDashboard;
    let { startDate, endDate } = analyticsGraph;

    let campaignIds,
      emailIds,
      workflowIds,
      fsId = getAnalyticsFilterId();
    const queryCampaignId = getQueryCampaignId();
    const queryEmailId = getQueryEmailId();

    if (filterReset === true || (searchReset === true && firstRender === false)) {
      campaignIds = appliedCampaignIds;
      emailIds = appliedAssetsIds;
      workflowIds = appliedWorkflowsIds;
    } else {
      if (!fsId && firstRender) {
        const isAIEmail = getQueryIsAIEmail();
        if (selectedCampaignIds.length <= 0 && queryCampaignId > 0 && firstRender === true) {
          campaignIds = selectedCampaignIds.length > 0 ? selectedCampaignIds : [queryCampaignId];
          if (isAIEmail) {
            const emailIds = getQueryEmailIds();
            emailIds.forEach((id: number) => {
              selectAsset(id, queryCampaignId);
            });
          } else {
            selectAsset(queryEmailId, queryCampaignId);
          }
          moveSelectedFiltersToApplied();
        } else campaignIds = appliedCampaignIds;
        if (appliedAssetsIds.length <= 0 && queryEmailId > 0 && firstRender === true) {
          if (isAIEmail) {
            emailIds = appliedAssetsIds.length > 0 ? appliedAssetsIds : getQueryEmailIds();
          } else {
            emailIds = appliedAssetsIds.length > 0 ? appliedAssetsIds : [queryEmailId];
          }
        } else emailIds = appliedAssetsIds;
        if (appliedWorkflowsIds.length <= 0 && getQueryWorkflowId() > 0 && firstRender === true) {
          workflowIds = appliedWorkflowsIds.length > 0 ? appliedWorkflowsIds : [getQueryWorkflowId()];
        } else workflowIds = appliedWorkflowsIds;
      } else {
        campaignIds = appliedCampaignIds;
        emailIds = appliedAssetsIds;
        workflowIds = appliedWorkflowsIds;
      }
    }

    if (startDate === '' && endDate === '') {
      if (queryCampaignId && queryEmailId) {
        analyticsDatesFilterChange({ label: 'All time', value: 'all' });
        const allTimeDate = getAllTimeDates();
        startDate = allTimeDate.start_date;
        endDate = allTimeDate.end_date;
      } else {
        analyticsDatesFilterChange(ANALYTICS_DEFAULT_DATES_FILTER);
        startDate = getQuarterDates().start_date;
        endDate = getQuarterDates().end_date;
      }
    }

    // check if is share profile || daily IQ || weekly accounts insights
    if (isShareProfile || isDailyIQ || isWeeklyAccountInsights) {
      let campaign_email_id = getConstantCampaignEmailId({ isShareProfile, isDailyIQ, isWeeklyAccountInsights });
      campaignIds = [campaign_email_id];
      emailIds = [campaign_email_id];
    }

    let _params: any = {
      allCampaigns: AllCampaignsFilterCheck,
      limit: 20,
      tab: tabValue ?? tab,
      searchWord: resetsearch === true ? '' : searchedText,
      offset: 0,
      emailIds: emailIds,
      workflowIds,
      startDate: startDate,
      endDate: endDate,
      campaignIds: campaignIds,
    };
    if (AllCampaignsFilterCheck) {
      _params['excludeCampaignsIds'] = excludeCampaignsIds;
      _params['excludeAssetsIds'] = excludeAssetsIds;
    }
    if (isAccount) {
      _params['accountId'] = getQueryAccountId();
    } else {
      if (getQueryAccountId() !== undefined) _params['accountId'] = getQueryAccountId();
    }
    if (contactsByFilter) _params['contactsByFilter'] = contactsByFilter;
    if (tabValue === 'bounced' && bounceFilter !== '') _params['bounce_filter'] = bounceFilter;
    if (tabValue === 'delivered') {
      if (deliveredSorting.accessor !== '') {
        _params['sortBy'] = deliveredSorting.accessor;
        _params['sortType'] = deliveredSorting.sortDirection;
      }
      if (query.get('opens') === 'true' && firstRender) {
        _params['contactsByFilter'] = 'TOTAL_OPENS > 0';
        store.dispatch({ type: CONTACT_ANALYTICS.SET_CONTACTS_BY_FILTER, payload: 'TOTAL_OPENS > 0' });
      }
      if (query.get('clicks') === 'true' && firstRender) {
        _params['contactsByFilter'] = 'TOTAL_CLICKS > 0';
        store.dispatch({ type: CONTACT_ANALYTICS.SET_CONTACTS_BY_FILTER, payload: 'TOTAL_CLICKS > 0' });
      }
    }
    if (tabValue === 'bounced') {
      if (bouncedSorting.accessor !== '') {
        _params['sortBy'] = bouncedSorting.accessor;
        _params['sortType'] = bouncedSorting.sortDirection;
      }
    }
    if (tabValue === 'unsubscribed') {
      if (unsubscribedSorting.accessor !== '') {
        _params['sortBy'] = unsubscribedSorting.accessor;
        _params['sortType'] = unsubscribedSorting.sortDirection;
      }
    }
    return _params;
  };

  const fetchAllContactsForTab = (tab?: string, resetSearch?: boolean, isAccount?: boolean, searchedText?: string, isFilterClicked?: boolean) => {
    getDeliveredContactsList(getParams('delivered', resetSearch, isAccount), isAccount);
    getBouncedContactsList(getParams('bounced', resetSearch, isAccount), isAccount);
    getUnsubscribedContactsList(getParams('unsubscribed', resetSearch, isAccount), isAccount);
    setTimeout(() => {
      store.dispatch({
        type: CONTACT_ANALYTICS.FILTER_RESET,
        payload: false,
      });
      store.dispatch({ type: CONTACT_ANALYTICS.SEARCH_RESET, payload: searchedText !== '' ? true : false });
    }, 500);
  };

  // useEffects
  useEffect(() => {
    if (firstRender === false) {
      resetAllContactProps();
      fetchAllContactsForTab(tab, false, accountId >= 0, searchedText);
      resetContactSearch();
    }
  }, [searchedText]);

  useEffect(() => {
    if (isFilterClicked === true) {
      store.dispatch({
        type: CONTACT_ANALYTICS.BOUNCED_RESET,
      });
      store.dispatch({ type: ANALYTICS_FILTERS.RESET });
      resetAllTabsFilter(true);
      resetAllContactProps();
      fetchAllContactsForTab(tab, false, accountId >= 0, searchedText, true);
    }
  }, [isFilterClicked]);

  useEffect(() => {
    let queryAccountId = getQueryAccountId();
    if (engagementEmails.length) {
      resetContactsEmailEngagement();
    }
    setFirstRender(false);
    if (((!total_delivered && !total_bounced && !total_unsubscribed) || searchReset === true || filterReset === true) && !isContactDashboard) {
      resetAllContactProps();
      fetchAllContactsForTab(tab, searchedText === '', Number(queryAccountId) >= 0, searchedText);
    }

    if (queryAccountId && queryAccountId !== accountId) {
      setAccountId(queryAccountId);
    }
    return () => {
      if (query.has('account_id')) {
        resetAllContactProps();
        if (isContactListing && !window.location.pathname.includes('/accounts')) {
          const params = new URLSearchParams(window.location.search);
          params.delete('account_id');
          history.replace(window.location.pathname + '?' + params);
        }
      }
      setBounceFilter('');
      setBounceFilterFirstTime(false);
    };
  }, []);

  return (
    <Wrapper className="contacts-deliverd">
      <div>
        {page === TAB_ANALYTICS.CONTACTS &&
          tab !== TAB_ANALYTICS_INNER.DASHBOARD &&
          tab !== TAB_ANALYTICS_INNER.CONTENT &&
          tab !== TAB_ANALYTICS.EMAIL_ENGAGEMENT && (
            <div className="search-field-container">
              <ContactsTabOption tabArray={tabArray} />
              <div className="d-flex" style={{ alignItems: 'center' }}>
                <div className="scaled-width-36 me-2 pe-1">
                  {(tab === undefined || tab === TAB_CONTACTS_ANALYTICS.DELIVERED) && (
                    <ContactsByFilterComponent searchedText={searchedText} accountId={accountId} filterLabelProp="Contacts by" />
                  )}
                  {(tab === undefined || tab === TAB_CONTACTS_ANALYTICS.BOUNCED) && (
                    <BounceTypeFilterComponent searchedText={searchedText} accountId={accountId} />
                  )}
                </div>
                <div className="scaled-width-35">
                  <SearchField
                    placeholder="Search Contacts..."
                    handleSearch={(value: string) => {
                      setSearchedText(value);
                    }}
                    reset={searchedText}
                  />
                </div>
              </div>
            </div>
          )}
        {tab === undefined || tab === TAB_CONTACTS_ANALYTICS.DELIVERED ? (
          <DeliveredContactsList tab={tab} searchedText={searchedText} accountId={accountId} setSearchedText={setSearchedText} />
        ) : tab === TAB_CONTACTS_ANALYTICS.BOUNCED ? (
          <BouncedContactsList tab={tab} searchedText={searchedText} accountId={accountId} setSearchedText={setSearchedText} />
        ) : tab === TAB_CONTACTS_ANALYTICS.UNSUBSCRIBED ? (
          <UnsubscribedContactsList tab={tab} searchedText={searchedText} accountId={accountId} setSearchedText={setSearchedText} />
        ) : tab === TAB_ANALYTICS_INNER.DASHBOARD ? (
          <ContactsDashboard tab={tab} />
        ) : tab === TAB_ANALYTICS.CONTENT ? (
          <ContactsContent tab={tab} />
        ) : tab === TAB_ANALYTICS.EMAIL_ENGAGEMENT ? (
          <EmailEngagement page={page} tab={tab} />
        ) : null}
        <ExportAnalyticsPopup page={'contacts'} searchedText={searchedText} />
      </div>
    </Wrapper>
  );
};

export default ContactsWrapper;

const Wrapper = styled.div`
  .search-field-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 5px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      margin-top: 7px;
      .wrapper {
        padding: 7px 16px;
        height: 30px !important;
      }
    }
    button .wrapper {
      white-space: nowrap;
      display: flex;
      align-items: center;
    }
  }
  .total-count {
    color: #7e7e7e;
    font-size: 14px;
    font-weight: 500;
    font-family: 'Roboto', sans-serif;
  }
  .scaled-width-35 {
    width: 200px;
    height: 38px;
  }
  .scaled-width-36 {
    width: 280px;
  }
`;
